//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DataTableConfig } from '@/__configs/config_types';
import { DeepPartial, IngestFile } from '@/redux/slices/slice_types';
import { defaultFilters, IngestManagementGridItemRender, NewFilterTypesMap } from '../../../exports/columnCommonComponents';

// baseUrl = the base url for the table that `/search` is appended to
// title = the title of the table that appears beneath the header/navbar
// label = the label used when referring to items in the table
// filters = filters that appear above the table
//           default options: status, managedBy, createdAt, filterId, search, jobType, stage,
//                            dueDate, completedAt, linearTxDate, qcByDate, qcOnPrem, customer,
//                            profile, assignedUsers, showResolved, showNotReady, filterIdNoJobAsset,
//                            fileType, entityType, showNotReady, showResolved
// preset = the default params to apply when the table is loaded that can be replaced by filters, such as sorting/ordering
// params = params to apply to the table when it is loaded that are applied to every request, cannot be replaced by filters
// disableExport = disable the export button when filters are applied
// parseDataFn = function to parse the data from the response before it is displayed in the table,
//               and should return the array of items to display in the table, eg. (data: any) => data?.response?.items

const dataConfig: DeepPartial<DataTableConfig> = {
  orders: {
    filters: [
      defaultFilters.status,
      defaultFilters.customer,
      defaultFilters.profile,
      defaultFilters.managedBy,
      defaultFilters.createdAt,
      defaultFilters.filterId,
      defaultFilters.search
    ]
  },
  jobs: {
    filters: [
      defaultFilters.jobType,
      defaultFilters.stage,
      defaultFilters.status,
      defaultFilters.dueDate,
      defaultFilters.createdAt,
      defaultFilters.completedAt,
      defaultFilters.managedBy,
      defaultFilters.customer,
      defaultFilters.profile,
      defaultFilters.filterId,
      defaultFilters.search
    ]
  },
  tasks: {
    preset: {
      sorting: ['priority', '-created_at']
    },
    filters: [
      defaultFilters.taskType,
      defaultFilters.status,
      defaultFilters.assignedUsers,
      defaultFilters.dueDate,
      defaultFilters.createdAt,
      defaultFilters.showNotReady,
      defaultFilters.filterIdNoJobAsset,
      {
        id: 'task_qc_screening_results_filter',
        label: 'QC Screening Results',
        type: NewFilterTypesMap.ARRAY,
        options: ({ constants }) => {
          return constants?.tasks?.task_qc_screening_results || [];
        }
      },
      defaultFilters.search
    ],
  },
  pendingAssets: {
    filters: [
      defaultFilters.status,
      defaultFilters.assignedGroups,
      defaultFilters.assignedUsers,
      defaultFilters.dueDate,
      defaultFilters.filterId,
      defaultFilters.search
    ]
  },
  titles: {
    baseUrl: '/inventory/titles/',
    label: 'titles',
    title: 'Titles',
    filters: [defaultFilters.entityTypeLabels, defaultFilters.search],
    preset: {
      sorting: {
        id: 'created_at',
        order: 'desc'
      },
      filters: { type: null }
    },
    params: {
      entity_types_filter: ['series', 'season', 'one_time_only', 'episode', 'compilation', 'supplemental'],
      thumbnails: true
    }
  },
  profiles: {
    disableExport: true
  },
  jobDetailTasks: {
    params: {
      name_filter: [
        'no_te_edit_task',
        'edit_task',
        'core_fix_edit_task',
        'tech_review_task',
        'tech_review_job_task',
        'internal_bcm_qc_task',
        'edm_internal_bcm_qc_task',
        'delivery_job'
      ]
    }
  },
  fileIngest: {
    baseUrl: '/ingest_manager/ingest_records/',
    enableGridViewToggle: false,
    disableVideoPreview: true,
    titleAssociation: {
      entity_types_filter: ['series', 'season', 'one_time_only', 'episode']
    },
    filters: [
      defaultFilters.fileType,
      defaultFilters.stage,
      defaultFilters.status,
      defaultFilters.createdAt,
      defaultFilters.ingestHid,
      defaultFilters.search
    ],
    gridItemRender: (row: IngestFile, _key: string, onGridClick) => {
      return <IngestManagementGridItemRender key={`row-${row.id}`} data={row} onGridClick={() => onGridClick(row.id)} />;
    }
  }
};

export default dataConfig;
